import React from 'react';
import {
    Box,
    CircularProgress,
    IconButton,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { UploaderField } from 'components/formik/uploader/uploader-field';

type DocumentName =
    | 'passport'
    | 'national'
    | 'selfie'
    | 'statute'
    | 'paper';

type DocumentUploaderComponentProps = {
    name: DocumentName;
    label: string;
    description?: string;
    formatsLabel: string;
    file: string;
    uploadUrl: string;
    isLoading: Record<DocumentName, boolean>;
    maxSize: number;
    allowedFormats: string[];
    allowedExtensions: string[];
    updateFile: (value: string) => void;
    setLoading: React.Dispatch<
        React.SetStateAction<Record<DocumentName, boolean>>
    >;
};

const normalizePreviewUrl = (file?: string) => {
    if (!file) return '';
    if (/^https?:\/\//i.test(file)) return file;

    if (file.startsWith('public/')) {
        const apiBase = (import.meta.env.VITE_APP_API_URL || '').replace(
            /\/api\/?$/,
            '',
        );
        return `${apiBase}/storage/${file.slice('public/'.length)}`;
    }

    return file;
};

const DocumentUploaderComponent = ({
    label,
    description,
    formatsLabel,
    name,
    file,
    uploadUrl,
    isLoading,
    maxSize,
    allowedFormats,
    allowedExtensions,
    updateFile,
    setLoading,
}: DocumentUploaderComponentProps) => {
    const previewUrl = normalizePreviewUrl(file);
    const isPdf = /\.pdf(?:$|\?)/i.test(previewUrl);
    const maxSizeMb = Math.round(maxSize / 1024 / 1024);

    return (
        <Box
            sx={(theme) => ({
                height: '100%',
                minHeight: 210,
                border: '1px solid',
                borderColor: theme.palette.divider,
                borderRadius: 1.5,
                overflow: 'hidden',
                backgroundColor: theme.palette.background.paper,
                transition: 'border-color .2s ease, box-shadow .2s ease',
                '&:hover': {
                    borderColor: alpha(theme.palette.primary.main, 0.45),
                    boxShadow: `0 8px 24px ${alpha(
                        theme.palette.common.black,
                        theme.palette.mode === 'dark' ? 0.16 : 0.05,
                    )}`,
                },
                '& > .MuiBox-root': {
                    height: '100%',
                },
                '& > .MuiBox-root > .MuiBox-root:first-of-type': {
                    minHeight: 210,
                    border: 0,
                    borderRadius: 0,
                    p: 0,
                },
            })}>
            <UploaderField
                disabled={Boolean(file)}
                url={uploadUrl}
                listDisable
                maxSize={maxSize}
                allowedFormats={allowedFormats}
                allowedExtensions={allowedExtensions}
                strictFilePicker
                onChangeFiles={(files?: any[]) => {
                    if (files?.length) {
                        const newFile = files[files.length - 1];
                        updateFile(newFile?.url || '');
                        setLoading((current) => ({
                            ...current,
                            [name]: newFile?.status === 'onUpload',
                        }));
                    } else {
                        updateFile('');
                        setLoading((current) => ({
                            ...current,
                            [name]: false,
                        }));
                    }
                }}>
                <Box
                    sx={{
                        width: '100%',
                        minHeight: 210,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                    }}>
                    {file && (
                        <IconButton
                            color="error"
                            size="small"
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                updateFile('');
                                setLoading((current) => ({
                                    ...current,
                                    [name]: false,
                                }));
                            }}
                            sx={(theme) => ({
                                position: 'absolute',
                                top: 10,
                                left: 10,
                                zIndex: 12,
                                width: 32,
                                height: 32,
                                backgroundColor: alpha(
                                    theme.palette.background.paper,
                                    0.9,
                                ),
                                border: '1px solid',
                                borderColor: theme.palette.divider,
                                '&:hover': {
                                    backgroundColor: theme.palette.error.main,
                                    color: theme.palette.error.contrastText,
                                },
                            })}>
                            <i className="fa-light fa-trash-xmark" />
                        </IconButton>
                    )}

                    <Box
                        sx={(theme) => ({
                            flex: 1,
                            minHeight: 142,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            backgroundColor:
                                theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.common.white, 0.025)
                                    : alpha(theme.palette.primary.main, 0.025),
                            borderBottom: '1px solid',
                            borderColor: theme.palette.divider,
                        })}>
                        {isLoading[name] ? (
                            <CircularProgress size={30} />
                        ) : file ? (
                            isPdf ? (
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        color: 'text.secondary',
                                    }}>
                                    <i
                                        className="fa-light fa-file-pdf"
                                        style={{ fontSize: 42 }}
                                    />
                                    <Typography sx={{ mt: 1, fontSize: 11.5 }}>
                                        فایل PDF بارگذاری شده
                                    </Typography>
                                </Box>
                            ) : (
                                <Box
                                    component="img"
                                    src={previewUrl}
                                    alt={label}
                                    sx={{
                                        width: '100%',
                                        height: 142,
                                        objectFit: 'cover',
                                        display: 'block',
                                    }}
                                />
                            )
                        ) : (
                            <Box
                                sx={{
                                    px: 2,
                                    textAlign: 'center',
                                    color: 'text.secondary',
                                }}>
                                <i
                                    className="fa-light fa-cloud-arrow-up"
                                    style={{ fontSize: 34 }}
                                />
                                <Typography
                                    sx={{
                                        mt: 1,
                                        fontSize: 12.5,
                                        color: 'text.primary',
                                        fontFamily: 'YekanBalkhHeavy',
                                    }}>
                                    انتخاب و بارگذاری فایل
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ px: 1.5, py: 1.15, minHeight: 66 }}>
                        <Typography
                            sx={{
                                fontSize: 12.5,
                                color: 'text.primary',
                                fontFamily: 'YekanBalkhHeavy',
                                lineHeight: 1.7,
                            }}>
                            {label}
                        </Typography>
                        {description && (
                            <Typography
                                sx={{
                                    mt: 0.15,
                                    fontSize: 10.5,
                                    color: 'text.secondary',
                                    lineHeight: 1.65,
                                }}>
                                {description}
                            </Typography>
                        )}
                        <Typography
                            sx={{
                                mt: 0.35,
                                fontSize: 9.75,
                                color: 'text.secondary',
                                direction: 'ltr',
                                textAlign: 'right',
                            }}>
                            {formatsLabel} · حداکثر {maxSizeMb}MB
                        </Typography>
                    </Box>
                </Box>
            </UploaderField>
        </Box>
    );
};

export default DocumentUploaderComponent;
